/**
 * IDDSI Framework v2.2 reference data (levels 0–7).
 *
 * Source: https://www.iddsi.org/images/Publications-Resources/DetailedDefnTestMethods/English/V2-2DetailedDefinitionsEnglishApr2026.pdf
 *
 * Levels 3 and 4 are dual-modality: the same number denotes different textures for
 * drinks versus foods ("Moderately Thick" / "Liquidised" and "Extremely Thick" /
 * "Pureed"). A flat 0–7 enum without modality is therefore unsafe — serving the
 * wrong name for 3 or 4 is a patient-safety failure, not a labelling quirk.
 *
 * Reference data only: no clinical decisions, no persistence, no imports from
 * elsewhere in the repo.
 */

export type IddsiModality = 'DRINK' | 'FOOD';

/** Drink thickness levels only; food-only levels 5–7 are excluded at the type level. */
export type IddsiDrinkLevel = 0 | 1 | 2 | 3 | 4;

/** Food texture levels only; drink-only levels 0–2 are excluded at the type level. */
export type IddsiFoodLevel = 3 | 4 | 5 | 6 | 7;

/** A prescribed IDDSI level is always modality-bound — never a bare number. */
export type IddsiRequirement =
  | { modality: 'DRINK'; level: IddsiDrinkLevel }
  | { modality: 'FOOD'; level: IddsiFoodLevel };

export const IDDSI_LEVELS: readonly {
  modality: IddsiModality;
  level: number;
  name: string;
}[] = [
  { modality: 'DRINK', level: 0, name: 'Thin' },
  { modality: 'DRINK', level: 1, name: 'Slightly Thick' },
  { modality: 'DRINK', level: 2, name: 'Mildly Thick' },
  { modality: 'DRINK', level: 3, name: 'Moderately Thick' },
  { modality: 'DRINK', level: 4, name: 'Extremely Thick' },
  { modality: 'FOOD', level: 3, name: 'Liquidised' },
  { modality: 'FOOD', level: 4, name: 'Pureed' },
  { modality: 'FOOD', level: 5, name: 'Minced & Moist' },
  { modality: 'FOOD', level: 6, name: 'Soft & Bite-Sized' },
  { modality: 'FOOD', level: 7, name: 'Regular' },
];

const DRINK_LEVELS: ReadonlySet<number> = new Set([0, 1, 2, 3, 4]);
const FOOD_LEVELS: ReadonlySet<number> = new Set([3, 4, 5, 6, 7]);

const NAME_BY_REQUIREMENT = new Map<string, string>(
  IDDSI_LEVELS.map((entry) => [`${entry.modality}:${entry.level}`, entry.name]),
);

export function isValidIddsiRequirement(modality: string, level: number): boolean {
  if (typeof level !== 'number' || !Number.isInteger(level)) {
    return false;
  }
  if (modality === 'DRINK') {
    return DRINK_LEVELS.has(level);
  }
  if (modality === 'FOOD') {
    return FOOD_LEVELS.has(level);
  }
  return false;
}

export function iddsiLevelName(req: IddsiRequirement): string {
  const name = NAME_BY_REQUIREMENT.get(`${req.modality}:${req.level}`);
  if (name === undefined) {
    throw new Error(`iddsi_level_name_missing:${req.modality}:${req.level}`);
  }
  return name;
}

/** Returns null on any invalid input — never throws and never assumes a modality. */
export function parseIddsiRequirement(modality: unknown, level: unknown): IddsiRequirement | null {
  if (modality !== 'DRINK' && modality !== 'FOOD') {
    return null;
  }
  if (typeof level !== 'number' || !Number.isInteger(level)) {
    return null;
  }
  if (!isValidIddsiRequirement(modality, level)) {
    return null;
  }
  if (modality === 'DRINK') {
    return { modality: 'DRINK', level: level as IddsiDrinkLevel };
  }
  return { modality: 'FOOD', level: level as IddsiFoodLevel };
}
